import type { FieldMetadata, WriteResult } from '../../../data/client';
import type { UpdateRecordVariables } from '../../../data/hooks';
import type { ToastInput } from '../../../ui';
import type { MoveTarget } from './moveTarget';

/**
 * What happens AFTER a card is dropped: one write, and the honest reading of its
 * three outcomes (INV-10 pending, INV-5 blocked, applied).
 *
 * This is deliberately not inlined in the board's `onDragEnd`. The three
 * outcomes are the part of a CRM board that is easiest to get wrong and hardest
 * to reach through a drag gesture, so they live in a function a test can call.
 */

/** The write, narrowed to what a move needs. `useUpdateRecord().mutate` fits it. */
export type MoveWriter = (
  variables: UpdateRecordVariables,
  callbacks: {
    readonly onSuccess: (result: WriteResult) => void;
    readonly onError: (error: Error) => void;
  },
) => void;

/**
 * WHAT THE THREE OUTCOMES NEED IN ORDER TO BE SAID OUT LOUD, and nothing else.
 *
 * Split out of `MoveContext` so the grouped TABLE's drop can reuse the reading without
 * inventing a board column to carry it (group-by-aggregation.md D24). The two surfaces
 * write different VALUES - a board writes an option id, a table group can also write a
 * boolean, a record id or null - but "queued for approval" and "someone owns this field"
 * mean exactly the same thing on both, and one copy of that sentence is the point.
 */
export interface MoveFeedbackContext {
  readonly groupField: FieldMetadata;
  /** The dropped record's title, for copy a user can recognise. */
  readonly recordTitle: string;
  /** Where it came from, in the words that were on screen. */
  readonly fromColumnLabel: string;
}

export interface MoveContext extends MoveFeedbackContext {
  readonly target: MoveTarget;
}

export interface MoveFeedback {
  /** `null` when there is nothing honest to say - the move simply applied. */
  readonly toast: ToastInput | null;
}

export function moveFeedback(context: MoveFeedbackContext, result: WriteResult): MoveFeedback {
  if (result.status === 'applied') {
    // Silence is the honest feedback: the card is already where it was dropped,
    // and a toast per drag would train people to ignore toasts.
    return { toast: null };
  }

  if (result.status === 'pending_approval') {
    /**
     * Nothing was written, and the card is already back: `useUpdateRecord` undoes the
     * optimistic value for this outcome, so the cache says what the DATABASE says. All that
     * is left to do is say why the card moved back.
     */
    return {
      toast: {
        title: 'Queued for approval',
        description: `${context.recordTitle} stays in ${context.fromColumnLabel} until someone approves the move.`,
        tone: 'neutral',
      },
    };
  }

  const holder = result.blocked.find((field) => field.fieldId === context.groupField.id)?.heldBy;
  const owner = holder?.displayName ?? holder?.userId ?? 'a person';
  // The hook wrote the record the server KEPT, so the cache already shows the card in
  // its original column; only the reason is missing.
  return {
    toast: {
      title: 'Move refused',
      description: `${context.groupField.label} is owned by ${owner}. The write was refused and the card moved back.`,
      tone: 'danger',
    },
  };
}

export interface ApplyMoveArgs {
  readonly context: MoveContext;
  readonly write: MoveWriter;
  readonly toast: (input: ToastInput) => void;
}

export function applyMove({ context, write, toast }: ApplyMoveArgs): void {
  write(
    {
      recordId: context.target.recordId,
      values: { [context.groupField.id]: context.target.optionId },
    },
    {
      onSuccess: (result) => {
        const feedback = moveFeedback(context, result);
        if (feedback.toast !== null) {
          toast(feedback.toast);
        }
      },
      onError: (error) => {
        // The hook has already rolled the optimistic value back, so the card is
        // home; all that is missing is saying so.
        toast({
          title: 'Could not move the record',
          description: error.message,
          tone: 'danger',
        });
      },
    },
  );
}
